/**
 * Click to enlarge a live design preview (Quotes and sayings, planners, Made in my town, Keep
 * Calm, London street signs), in the manner of the Create Your Own lightbox.
 *
 * The previews are drawings, not photographs, so there is no large image to open. Instead the
 * board is copied into a full-screen dialog and drawn again at that size:
 *
 *   <div data-btf-enlarge="btf-planner-board" data-btf-enlarge-caption="Your planner">
 *     <btf-planner-board …></btf-planner-board>
 *     <button type="button" class="btf-enlarge-btn" data-btf-enlarge-open>Enlarge</button>
 *   </div>
 *
 * - data-btf-enlarge names what to copy inside the stage; the first one showing is used.
 * - data-btf-enlarge-strip names parts of the copy to remove first, where the element would
 *   otherwise draw them a second time (the street sign's type layer).
 * - data-btf-enlarge-click="button" opens it from the button only, where a click or drag on the
 *   board already has a job (Create Your Own: dragging reframes the photograph).
 * - The copy loses its ids and data-max, so a board fills the dialog, and it is inert.
 * - The dialog is placed beside the stage, so the colours, fonts and sizes the page has set on
 *   the section (custom properties) still apply to the copy. showModal() lifts it above the page.
 *
 * Clicking the stage or its Enlarge button opens it; Escape, the cross at the top right, the Close
 * button or a click on the backdrop closes it, and focus goes back to the button.
 */

let dialog = null;
let opener = null;

function build() {
  const d = document.createElement('dialog');
  d.className = 'btf-enlarge';
  d.setAttribute('aria-label', 'Enlarged preview');
  d.innerHTML =
    '<button type="button" class="btf-enlarge__x" aria-label="Close">' +
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 4l16 16M20 4L4 20" fill="none" stroke="currentColor" stroke-width="2.5"/></svg></button>' +
    '<div class="btf-enlarge__view"></div>' +
    '<div class="btf-enlarge__row"><span class="btf-enlarge__caption"></span>' +
    '<button type="button" class="btf-enlarge__close">Close</button></div>';
  d.addEventListener('click', (e) => {
    if (e.target === d || e.target.closest('.btf-enlarge__close, .btf-enlarge__x')) d.close();
  });
  d.addEventListener('close', () => {
    d.querySelector('.btf-enlarge__view').replaceChildren();
    if (opener && opener.isConnected) opener.focus({ preventScroll: true });
    opener = null;
  });
  return d;
}

function visible(el) {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function open(stage, from) {
  const pick = Array.from(stage.querySelectorAll(stage.dataset.btfEnlarge || ':scope > *')).find(visible);
  if (!pick) return;
  if (!dialog) dialog = build();
  // Beside the stage, so the section's custom properties reach the copy
  stage.after(dialog);

  const copy = pick.cloneNode(true);
  if (stage.dataset.btfEnlargeStrip) copy.querySelectorAll(stage.dataset.btfEnlargeStrip).forEach((n) => n.remove());
  [copy, ...copy.querySelectorAll('[id]')].forEach((n) => n.removeAttribute('id'));
  copy.removeAttribute('data-max');
  // A board that sizes itself on the page (Create Your Own sets its own width) fills the dialog
  ['width', 'max-width', 'height'].forEach((prop) => copy.style.removeProperty(prop));
  copy.setAttribute('inert', '');

  // A photograph that names a larger file (data-full, the article template) opens that file
  // whole: the page may show it cropped, as the article's half-width photographs are
  const full = copy.dataset.full;
  if (full) {
    copy.src = full;
    ['srcset', 'sizes', 'style', 'width', 'height'].forEach((a) => copy.removeAttribute(a));
    copy.loading = 'eager';
  }

  // As large as the screen allows at the board's own proportions, leaving room for the Close row
  const r = pick.getBoundingClientRect();
  const ratio = full && pick.naturalWidth ? pick.naturalWidth / pick.naturalHeight : r.width / r.height;
  const w = Math.min(window.innerWidth - 48, (window.innerHeight - 120) * ratio);
  const view = dialog.querySelector('.btf-enlarge__view');
  view.style.width = Math.floor(w) + 'px';
  // A photograph asks for a file at the dialog's size, not the tile's
  if (copy.matches('img[sizes]')) {
    copy.sizes = Math.floor(w) + 'px';
    copy.loading = 'eager';
  }
  view.replaceChildren(copy);
  dialog.querySelector('.btf-enlarge__caption').textContent = stage.dataset.btfEnlargeCaption || '';

  opener = from || stage.querySelector('[data-btf-enlarge-open]');
  dialog.showModal();
  dialog.querySelector('.btf-enlarge__close').focus({ preventScroll: true });

  // A board draws itself on the next frame, and its drawing need not have the shape of the
  // element on the page (a board capped by data-max sits narrower than its box). Once drawn,
  // anything taller than the room left is brought down to fit.
  const fit = (tries) =>
    requestAnimationFrame(() => {
      const room = window.innerHeight - 120;
      const h = copy.getBoundingClientRect().height;
      if (h > room + 1) view.style.width = Math.floor(view.getBoundingClientRect().width * (room / h)) + 'px';
      if (tries > 1) fit(tries - 1);
    });
  fit(3);
}

document.addEventListener('click', (e) => {
  const t = e.target instanceof Element ? e.target : null;
  const stage = t && t.closest('[data-btf-enlarge]');
  if (!stage || t.closest('.btf-enlarge')) return;
  if (stage.dataset.btfEnlargeClick === 'button' && !t.closest('[data-btf-enlarge-open]')) return;
  // Controls inside a stage (view tabs, badges that link) keep their own job
  const control = t.closest('a, input, select, textarea, label, button:not([data-btf-enlarge-open])');
  if (control && stage.contains(control)) return;
  e.preventDefault();
  open(stage, t.closest('[data-btf-enlarge-open]'));
});
